import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Search, Sparkles, Sliders, Bot, MessageSquare, Wand2, Crown,
  PenLine, Code, Microscope, Film, Briefcase, Megaphone, Zap,
  BarChart3, Palette, GraduationCap, DollarSign, Gem, Target,
  ArrowRight, CornerDownLeft, Command as CmdIcon,
} from "lucide-react";
import { ToolLogo } from "@/components/ToolLogo";

/**
 * ⌘K Command Palette — the ambient, intent-driven navigation layer.
 *
 * A single input handles natural-language search across every corner of the app:
 * tools, agents, pages, AI actions. Results are grouped, keyboard-first, and
 * updated live. Opens with ⌘K / Ctrl+K anywhere on the site.
 */

// Static pages — actions and destinations the palette can jump to instantly.
const STATIC_ACTIONS = [
  { id: "act-ask-genius",   label: "Wikifi anything",    hint: "Say what you need, we hand-pick 3 tools", path: "/ask",     icon: Sparkles, kind: "action" },
  { id: "act-create-yt",    label: "Create a YouTube video",  hint: "Studio · YouTube template",  path: "/create",   icon: Sparkles, kind: "action" },
  { id: "act-create-ad",    label: "Create an ad campaign",   hint: "Studio · Ad Campaign",       path: "/create",   icon: Sparkles, kind: "action" },
  { id: "act-create-deck",  label: "Create a pitch deck",     hint: "Studio · Pitch Deck",        path: "/create",   icon: Sparkles, kind: "action" },
  { id: "act-algorithm",    label: "Analyze a URL with Algorithm", hint: "Paste any YT/social link", path: "/algorithm", icon: Target, kind: "action" },
  { id: "act-chat-arena",   label: "Compare answers across GPT · Claude · Gemini", hint: "Multi-model chat",   path: "/chat",     icon: MessageSquare, kind: "action" },
  { id: "act-quiz",         label: "Take the AI tools quiz",  hint: "Get 5 personalised picks",    path: "/quiz",     icon: Wand2, kind: "action" },

  { id: "nav-home",         label: "All Tools",               hint: "Encyclopedia home",           path: "/",         icon: Sliders,     kind: "nav" },
  { id: "nav-agents",       label: "AI Agents",               hint: "Browsable agent catalog",     path: "/agents",   icon: Bot,         kind: "nav" },
  { id: "nav-prompts",      label: "Prompt Library",          hint: "Curated professional prompts",  path: "/prompts",  icon: PenLine,     kind: "nav" },
  { id: "nav-humanize",     label: "Humanize text or images", hint: "Sound less AI",               path: "/humanize", icon: Sparkles,    kind: "nav" },
  { id: "nav-pricing",      label: "Pricing",                 hint: "Curious · Creator · All Access", path: "/pricing", icon: Crown,    kind: "nav" },
  { id: "nav-trends",       label: "Visual Trends",           hint: "What's hitting right now",    path: "/trends",   icon: Palette,     kind: "nav" },
  { id: "nav-gems",         label: "Hidden Gems",             hint: "Underrated tools",            path: "/gems",     icon: Gem,         kind: "nav" },

  { id: "cat-writing",      label: "Writing tools",           hint: "Category",                    path: "/?cat=Writing+%26+Copy",       icon: PenLine, kind: "category" },
  { id: "cat-coding",       label: "Coding tools",            hint: "Category",                    path: "/?cat=Coding+%26+Dev",         icon: Code,    kind: "category" },
  { id: "cat-research",     label: "Research tools",          hint: "Category",                    path: "/?cat=Research",               icon: Microscope, kind: "category" },
  { id: "cat-creative",     label: "Creative & Film",         hint: "Category",                    path: "/?cat=Creative+%2F+Film",      icon: Film,    kind: "category" },
  { id: "cat-business",     label: "Business tools",          hint: "Category",                    path: "/?cat=Business+%26+Marketing", icon: Briefcase, kind: "category" },
  { id: "cat-marketing",    label: "Marketing tools",         hint: "Category",                    path: "/?search=marketing",           icon: Megaphone, kind: "category" },
  { id: "cat-productivity", label: "Productivity tools",      hint: "Category",                    path: "/?cat=Productivity",           icon: Zap,     kind: "category" },
  { id: "cat-analysis",     label: "Data analysis tools",     hint: "Category",                    path: "/?search=analysis",            icon: BarChart3, kind: "category" },
  { id: "cat-design",       label: "Design tools",            hint: "Category",                    path: "/?cat=Design+%26+Art",         icon: Palette, kind: "category" },
  { id: "cat-education",    label: "Education tools",         hint: "Category",                    path: "/?cat=Education",              icon: GraduationCap, kind: "category" },
  { id: "cat-finance",      label: "Finance tools",           hint: "Category",                    path: "/?cat=Finance",                icon: DollarSign, kind: "category" },
  { id: "cat-assistant",    label: "AI assistants",           hint: "Category",                    path: "/?cat=Chat+%26+LLMs",          icon: MessageSquare, kind: "category" },
];

const GROUP_ORDER = ["action", "tool", "agent", "nav", "category"];
const GROUP_LABEL = {
  action:   "AI Actions",
  tool:     "Tools",
  agent:    "Agents",
  nav:      "Pages",
  category: "Categories",
};

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [tools, setTools] = useState([]);
  const [agents, setAgents] = useState([]);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();

  // Global open shortcut: ⌘K / Ctrl+K
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(o => !o);
        return;
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Focus input & preload catalog when opened.
  useEffect(() => {
    if (!open) return;
    setCursor(0);
    setQ("");
    // Autofocus after mount
    setTimeout(() => inputRef.current?.focus(), 50);
    if (tools.length === 0) {
      api.get("/tools").then(r => setTools(r.data.slice(0, 200))).catch(() => {});
      api.get("/agents").then(r => setAgents(r.data.slice(0, 50))).catch(() => {});
    }
    // Lock scroll behind palette
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [open]);  // eslint-disable-line

  const results = useMemo(() => {
    const query = q.trim().toLowerCase();
    // Stopwords we drop before matching so "best AI for realistic video" doesn't
    // fail on words like "best" or "for". Keeps signal words like "realistic" & "video".
    const STOP = new Set(["a","an","the","and","or","of","for","to","with","in","on","at",
                          "is","are","was","were","be","best","top","good","great","ai",
                          "app","apps","tool","tools","me","my","i","we","us","you","your",
                          "please","help","how","what","which","find","need","want","looking",
                          "make","making","create","creating","get","using","use","that",
                          "this","from","when","some","any"]);
    const tokens = query.split(/\s+/).filter(t => t && !STOP.has(t));

    const matches = (label, hint = "") => {
      if (!query) return true;
      const hay = `${label} ${hint}`.toLowerCase();
      if (tokens.length === 0) {
        // Everything was stopwords — fall back to substring against raw query
        return hay.includes(query);
      }
      // Strong match if EVERY signal token is present…
      const allHit = tokens.every(t => hay.includes(t));
      if (allHit) return true;
      // …otherwise soft-match: any signal token present is a candidate.
      return tokens.some(t => t.length >= 3 && hay.includes(t));
    };

    const toolResults = tools
      .filter(t => matches(t.name, `${t.tagline || ""} ${t.category || ""} ${(t.brief || "").slice(0, 400)}`))
      .slice(0, 8)
      .map(t => ({
        id: `tool-${t.slug}`, label: t.name, hint: t.tagline || t.category,
        path: `/tool/${t.slug}`, kind: "tool", url: t.url, logo_color: t.logo_color, logo_letter: t.logo_letter,
      }));

    const agentResults = agents
      .filter(a => matches(a.name, `${a.tagline || ""} ${(a.brief || "").slice(0, 400)}`))
      .slice(0, 4)
      .map(a => ({
        id: `agent-${a.slug}`, label: a.name, hint: a.tagline,
        path: `/tool/${a.slug}`, kind: "agent", url: a.url, logo_color: a.logo_color, logo_letter: a.logo_letter,
      }));

    const staticResults = STATIC_ACTIONS.filter(s => matches(s.label, s.hint));

    // Order: actions first (only when there's a query), then tools, agents, nav, categories.
    // With no query: prioritise actions + nav for discovery.
    const combined = query
      ? [...staticResults.filter(s => s.kind === "action"), ...toolResults, ...agentResults, ...staticResults.filter(s => s.kind !== "action")]
      : [...staticResults.filter(s => s.kind === "action").slice(0, 6),
         ...staticResults.filter(s => s.kind === "nav"),
         ...staticResults.filter(s => s.kind === "category")];

    // Group them for display while keeping cursor index across groups.
    const grouped = {};
    combined.forEach(r => {
      const k = r.kind || "nav";
      (grouped[k] = grouped[k] || []).push(r);
    });
    // Flat list for keyboard nav
    const flat = [];
    GROUP_ORDER.forEach(k => (grouped[k] || []).forEach(r => flat.push(r)));
    return { grouped, flat };
  }, [q, tools, agents]);

  const total = results.flat.length;

  const select = useCallback((item) => {
    if (!item) return;
    setOpen(false);
    // Free-text fallback: user typed something but no result — search the directory
    if (item === "free_text_search" && q.trim()) {
      navigate(`/?search=${encodeURIComponent(q.trim())}`);
      return;
    }
    navigate(item.path);
  }, [q, navigate]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setCursor(c => (c + 1) % Math.max(1, total)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setCursor(c => (c - 1 + Math.max(1, total)) % Math.max(1, total)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (total === 0 && q.trim()) select("free_text_search");
      else select(results.flat[cursor]);
    }
  };

  if (!open) return null;

  let index = -1;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[10vh] px-4 bg-black/40 backdrop-blur-sm"
         onClick={() => setOpen(false)}
         data-testid="command-palette">
      <div className="w-full max-w-2xl bg-white dark:bg-[#0B1220] border border-[#E3E8F0] dark:border-[#1E293B] rounded-2xl shadow-2xl overflow-hidden"
           onClick={e => e.stopPropagation()}>
        {/* Search input */}
        <div className="flex items-center gap-3 border-b border-[#E3E8F0] dark:border-[#1E293B] px-4 py-3">
          <Search size={18} className="text-[#5B6478] flex-shrink-0"/>
          <input
            ref={inputRef}
            value={q}
            onChange={e => { setQ(e.target.value); setCursor(0); }}
            onKeyDown={onKeyDown}
            placeholder="Ask anything, jump anywhere…  (e.g. 'best AI for realistic video with audio')"
            className="flex-1 bg-transparent focus:outline-none text-base"
            data-testid="command-palette-input"
          />
          <span className="text-[10px] font-mono text-[#5B6478] flex items-center gap-1 border border-[#E3E8F0] dark:border-[#1E293B] rounded px-1.5 py-0.5">
            <CmdIcon size={9}/>K
          </span>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[60vh] overflow-y-auto p-2" data-testid="command-palette-results">
          {total === 0 ? (
            <div className="text-center py-8">
              {q.trim() ? (
                <>
                  <div className="text-sm text-[#5B6478]">No direct match. Press ↵ to search the directory for</div>
                  <div className="text-base font-semibold mt-1">"{q.trim()}"</div>
                </>
              ) : (
                <div className="text-sm text-[#5B6478]">Start typing to jump anywhere.</div>
              )}
            </div>
          ) : (
            GROUP_ORDER.map(kind => {
              const items = results.grouped[kind];
              if (!items || items.length === 0) return null;
              return (
                <div key={kind} className="mb-1">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-[#5B6478] px-3 py-2">
                    {GROUP_LABEL[kind]}
                  </div>
                  {items.map(item => {
                    index += 1;
                    const active = index === cursor;
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.id}
                        onClick={() => select(item)}
                        onMouseEnter={() => setCursor(index)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                          active ? "bg-[#F7F9FC] dark:bg-[#111827]" : ""
                        }`}
                        data-testid={`cmd-item-${item.id}`}
                      >
                        {Icon ? (
                          <div className="w-8 h-8 rounded-lg bg-[#F7F9FC] dark:bg-[#1E293B] flex items-center justify-center flex-shrink-0">
                            <Icon size={14} className="text-[#0B1220] dark:text-white"/>
                          </div>
                        ) : (
                          <ToolLogo tool={item} size={32} rounded="lg" className="flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-[#0B1220] dark:text-white truncate">{item.label}</div>
                          {item.hint && <div className="text-xs text-[#5B6478] truncate">{item.hint}</div>}
                        </div>
                        {active && <CornerDownLeft size={12} className="text-[#5B6478]"/>}
                      </button>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-[#E3E8F0] dark:border-[#1E293B] px-4 py-2 flex items-center justify-between text-[11px] text-[#5B6478]">
          <div className="flex items-center gap-3">
            <span><kbd className="font-mono">↑↓</kbd> Navigate</span>
            <span><kbd className="font-mono">↵</kbd> Open</span>
            <span><kbd className="font-mono">esc</kbd> Close</span>
          </div>
          <div className="flex items-center gap-1 font-semibold text-coral">
            <Sparkles size={11}/> Powered by Wikifi
          </div>
        </div>
      </div>
    </div>
  );
}
